import { FC, useCallback, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { PaginationState } from "@tanstack/react-table"
import { BBColumnDef, Integration } from "../../../../types"
import Table from "../../table"
import PreviewModal from "../../preview-modal"
import { useAppDispatch, useInMobileView } from "../../../hooks"
import { setPreviewModal } from "../../../../redux/slices/integrations"
import { IntegrationCard, IntegrationCell } from "../table/cells"
import IntegrationCardSkeleton from "../table/components/IntegrationCardSkeleton"
import { ListSkeleton } from "../../skeletons"
import If from "../../if"

interface Props {
  data: Integration[]
  isLoading: boolean
  onSelect?: (id: string) => void
  totalCount?: number
  pagination?: PaginationState
  setPagination?: React.Dispatch<React.SetStateAction<PaginationState>>
}

const IntegrationTable: FC<Props> = ({
  data,
  isLoading = false,
  onSelect,
  totalCount = 0,
  pagination,
  setPagination
}) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const isMobile = useInMobileView()
  const listViewType = isMobile ? "grid" : "table"

  const handleSelectIntegration = useCallback(
    (id: string) => {
      if (onSelect) {
        onSelect(id)
      } else {
        navigate(`/integrations/${id}`)
      }
    },
    [onSelect, navigate]
  )

  const pageCount = useMemo(() => {
    if (!pagination || !totalCount) return undefined
    return Math.ceil(totalCount / pagination.pageSize)
  }, [totalCount, pagination])

  const columns: BBColumnDef<Integration>[] = useMemo(
    () => [
      {
        id: "title",
        header: () => <>Name</>,
        footer: props => props.column.id,
        cell: info => (
          <IntegrationCell
            data={info.row.original}
            onSelect={handleSelectIntegration}
          />
        )
      }
    ],
    [handleSelectIntegration]
  )

  return (
    <div className="mt-6">
      <If
        condition={isLoading}
        then={
          isMobile ? (
            <div className="flex flex-col gap-3 py-2 mt-4">
              {[...Array(4)].map((_, i) => (
                <IntegrationCardSkeleton key={i} />
              ))}
            </div>
          ) : (
            <ListSkeleton />
          )
        }
        else={
          <div className="px-4">
            <Table
              className="mt-4 -mb-6"
              data={data}
              columns={columns}
              viewType={listViewType}
              pageSize={pagination?.pageSize || 10}
              pageCount={pageCount}
              pagination={pagination}
              setPagination={setPagination}
              rowCount={totalCount}
              searchBarVisible={false}
              renderCard={props => (
                <IntegrationCard
                  data={props.original}
                  onSelect={handleSelectIntegration}
                />
              )}
              gridClass="flex flex-col gap-3 py-2"
              cardClassName="text-left bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden"
              noHeader
              removeMobilePagination
            />
            <PreviewModal
              onClose={() => {
                dispatch(
                  setPreviewModal({
                    isOpen: false,
                    appKey: ""
                  })
                )
              }}
            />
          </div>
        }
      />
    </div>
  )
}
export default IntegrationTable
